import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import api from "../../api";
import { actions } from "../../redux/_actions";
import { Constants } from "../../redux/_constants";
import utils from "../../utils";

const Signin = ({ isOpen, closeModal }) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  const { verifying, secure_identity, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const { addToast } = useToasts();

  const showToast = useCallback(
    (message, appearance = "error") => {
      addToast(message, { appearance, autoDismiss: true });
    },
    [addToast]
  );

  useEffect(() => {
    if (error) {
      showToast(error, "error");
    }
  }, [error, showToast]);

  // The request finished one way or the other.
  useEffect(() => {
    setSubmitting(false);
  }, [verifying, error]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeModal]);

  useEffect(() => {
    if (!verifying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    const checkSecureVerified = () => {
      api
        .checkSecureVerified(secure_identity)
        .then((data) => {
          if (data.success) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            dispatch(actions.verifySignin({ uid: secure_identity }));
          }
        })
        .catch((err) => {
          showToast(err.message);
        });
    };

    intervalRef.current = setInterval(checkSecureVerified, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [verifying, secure_identity, dispatch, showToast]);

  const signin = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!value || !value.trim()) {
      showToast("Please enter your email address", "error");
      return;
    }
    if (!utils.validateEmail(value)) {
      showToast("Please enter a valid email address", "error");
      return;
    }
    setSubmitting(true);
    dispatch(actions.signin({ email: value.trim() }));
  };

  // Back to the email field (clears the pending link check).
  const changeEmail = () => dispatch({ type: Constants.SIGNOUT });

  if (!isOpen) {
    return null;
  }

  const modal = (
    <div
      className="signup-modal open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="signup-modal__close"
        onClick={closeModal}
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div className="background-img">
        <img
          src="/Modal.svg"
          alt=""
          width={1000}
          height={1000}
        />
      </div>
      {verifying ? (
        <div className="signup-modal__sent" role="status">
          <div className="signup-modal__sent-icon" aria-hidden="true">✉️</div>
          <h1 id="signin-modal-title">Check your email</h1>
          <p>
            We sent a sign-in link to <strong>{value || "your email"}</strong>.
            Tap it to finish. This page signs you in on its own once you do.
          </p>
          <div className="signup-modal__sent-actions">
            <button type="button" className="button" onClick={signin} disabled={submitting || !value}>
              {submitting ? "Sending…" : "Resend link"}
            </button>
            <button type="button" className="signup-modal__link" onClick={changeEmail}>
              Use a different email
            </button>
          </div>
          <p className="signup-modal__hint">Can't find it? Check spam or promotions.</p>
        </div>
      ) : (
        <form onSubmit={signin}>
          <div className="headings">
            <div className="logo-heading">
              <img
                src="/ollcart-icon-128-79.png"
                alt="OllaCart"
                width={204}
                height={67}
                style={{ objectFit: "cover" }}
              />
              <h1 id="signin-modal-title">Sign in or create account</h1>
            </div>
            <p>One cart for every store. No password: we email you a link.</p>
          </div>
          <div className="form-wrap">
            <div className="input-container">
              <label htmlFor="signin-email">Email</label>
              <input
                id="signin-email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                placeholder="you@example.com"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
          </div>
          <button type="submit" className="button" disabled={submitting}>
            {submitting ? "Sending link…" : "Continue with email"}
          </button>
          <p className="signup-modal__legal">
            By continuing, you agree to our{" "}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a> and{" "}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </p>
        </form>
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default Signin;
